import React, { useState, useCallback, useEffect } from "react";
import {
  Table, Input, Select, DatePicker, Button,
  Tag, Badge, Tooltip, Typography, Row, Col, Avatar, Space,
} from "antd";
import {
  SearchOutlined, ClearOutlined,
  EyeOutlined, FileExcelOutlined, FilterOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";

const { Text }        = Typography;
const { RangePicker } = DatePicker;
const { Option }      = Select;

// ── Paleta de colores ──────────────────────────────────────────────────────
const E_COLOR = { ART: "blue",   FAO: "cyan",   ADMIN: "purple" };
const E_BG    = { ART: "#e8f4ff", FAO: "#e6fffb", ADMIN: "#f9f0ff" };
const E_TXT   = { ART: "#0958d9", FAO: "#087c7c", ADMIN: "#531dab" };

// ── Sub-componentes ────────────────────────────────────────────────────────

const CounterCell = ({ value, tooltip }) => (
  <Tooltip title={tooltip} placement="top">
    <span
      style={{
        display: "inline-block",
        minWidth: 36,
        padding: "2px 6px",
        borderRadius: 4,
        textAlign: "center",
        fontWeight: value > 0 ? 600 : 400,
        fontSize: 13,
        color:      value > 0 ? "#1677ff" : "#bfbfbf",
        background: value > 0 ? "#f0f5ff" : "transparent",
        cursor: "default",
      }}
    >
      {value ?? 0}
    </span>
  </Tooltip>
);

const ColTitle = ({ label, tip }) => (
  <Tooltip title={tip} placement="top">
    <span style={{ cursor: "help", borderBottom: "1px dashed #c0c0c0", fontSize: 12 }}>
      {label}
    </span>
  </Tooltip>
);

// ── Columnas ───────────────────────────────────────────────────────────────
const buildColumns = (onViewDetail, onExportTrace, permissionExport) => [
  {
    title:  "Usuario",
    key:    "usuario",
    width:  220,
    fixed:  "left",
    render: (_, r) => {
      const initial = (r.name || r.login || "?")[0].toUpperCase();
      return (
        <Space size={10}>
          <Avatar
            size={34}
            style={{ background: E_BG[r.entidad] || "#f5f5f5", color: E_TXT[r.entidad] || "#595959", fontWeight: 700, fontSize: 14, flexShrink: 0 }}
          >
            {initial}
          </Avatar>
          <div style={{ lineHeight: 1.4, minWidth: 0 }}>
            <div style={{ fontWeight: 600, fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 150 }}>
              {r.name}
            </div>
            <Text type="secondary" style={{ fontSize: 11 }}>@{r.login}</Text>
          </div>
        </Space>
      );
    },
  },
  {
    title:     "Entidad",
    dataIndex: "entidad",
    key:       "entidad",
    width:     80,
    align:     "center",
    render: (v) => v
      ? <Tag color={E_COLOR[v] || "default"} style={{ margin: 0, fontSize: 11 }}>{v}</Tag>
      : <Text type="secondary">—</Text>,
  },
  {
    title:     "Estado",
    dataIndex: "active",
    key:       "active",
    width:     90,
    align:     "center",
    render: (v) => v === "Y"
      ? <Badge status="success" text={<Text style={{ fontSize: 11 }}>Activo</Text>} />
      : v === "N"
      ? <Badge status="warning" text={<Text style={{ fontSize: 11 }}>Inactivo</Text>} />
      : <Badge status="default"  text={<Text style={{ fontSize: 11 }}>—</Text>} />,
  },
  {
    title:  <ColTitle label="Último ingreso" tip="Fecha y hora del último evento de sesión registrado" />,
    dataIndex: "ultimo_login",
    key:    "ultimo_login",
    width:  140,
    render: (v) => v
      ? <Text style={{ fontSize: 12 }}>{dayjs(v).format("DD/MM/YY HH:mm")}</Text>
      : <Text type="secondary" style={{ fontSize: 11 }}>Sin registro</Text>,
  },
  {
    title:  <ColTitle label="Ingresos" tip="Eventos de sesión registrados automáticamente" />,
    dataIndex: "total_ingresos",
    key:    "total_ingresos",
    width:  90,
    align:  "center",
    sorter: (a, b) => a.total_ingresos - b.total_ingresos,
    render: (v) => <CounterCell value={v} tooltip="Total eventos de sesión" />,
  },
  {
    title:  <ColTitle label="Acciones" tip="Acciones registradas por módulo con IP" />,
    dataIndex: "total_acciones_plataforma",
    key:    "total_acciones_plataforma",
    width:  90,
    align:  "center",
    sorter: (a, b) => a.total_acciones_plataforma - b.total_acciones_plataforma,
    render: (v) => <CounterCell value={v} tooltip="Acciones en plataforma" />,
  },
  {
    title:  <ColTitle label="Docs POA" tip="Medios de verificación cargados" />,
    dataIndex: "total_poa_documentos",
    key:    "total_poa_documentos",
    width:  95,
    align:  "center",
    sorter: (a, b) => a.total_poa_documentos - b.total_poa_documentos,
    render: (v) => <CounterCell value={v} tooltip="Documentos POA subidos" />,
  },
  {
    title:  <ColTitle label="Novedades" tip="Novedades de participantes registradas" />,
    dataIndex: "total_novedades",
    key:    "total_novedades",
    width:  100,
    align:  "center",
    sorter: (a, b) => a.total_novedades - b.total_novedades,
    render: (v) => <CounterCell value={v} tooltip="Novedades de participantes" />,
  },
  {
    title:  <ColTitle label="Repositorio" tip="Carpetas y archivos creados" />,
    dataIndex: "total_repo_documentos",
    key:    "total_repo_documentos",
    width:  105,
    align:  "center",
    sorter: (a, b) => a.total_repo_documentos - b.total_repo_documentos,
    render: (v) => <CounterCell value={v} tooltip="Documentos en repositorio" />,
  },
  {
    title:  "Acciones",
    key:    "acciones",
    width:  permissionExport ? 90 : 54,
    fixed:  "right",
    align:  "center",
    render: (_, r) => (
      <Space size={4}>
        <Tooltip title="Ver detalle y actividad del usuario">
          <Button
            type="primary"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => onViewDetail(r.oid)}
            style={{ fontSize: 12 }}
          />
        </Tooltip>
        {permissionExport && (
          <Tooltip title="Descargar trazabilidad completa en Excel">
            <Button
              size="small"
              icon={<FileExcelOutlined />}
              onClick={() => onExportTrace(r.oid, r.login)}
              style={{ color: "#389e0d", borderColor: "#b7eb8f" }}
            />
          </Tooltip>
        )}
      </Space>
    ),
  },
];

// ── Componente ─────────────────────────────────────────────────────────────
const TablaUsuarios = ({
  data, total, loading, filters, permissionExport,
  onFiltersChange, onViewDetail, onExportTrace,
}) => {
  const [localLogin, setLocalLogin] = useState(filters.login || "");

  useEffect(() => {
    if (!filters.login) setLocalLogin("");
  }, [filters.login]);

  const handleSearch = useCallback(() => {
    onFiltersChange({ login: localLogin.trim() || undefined, page: 1 });
  }, [localLogin, onFiltersChange]);

  const handleDateRange = useCallback((dates) => {
    onFiltersChange({
      dateFrom: dates?.[0]?.format("YYYY-MM-DD") || undefined,
      dateTo:   dates?.[1]?.format("YYYY-MM-DD") || undefined,
      page: 1,
    });
  }, [onFiltersChange]);

  const handleClear = () => {
    setLocalLogin("");
    onFiltersChange({
      login: undefined, entidad: undefined, active: undefined,
      dateFrom: undefined, dateTo: undefined, page: 1,
    });
  };

  const hasFilters = filters.login || filters.entidad || filters.active || filters.dateFrom;
  const dateValue  = filters.dateFrom && filters.dateTo
    ? [dayjs(filters.dateFrom), dayjs(filters.dateTo)]
    : undefined;

  return (
    <>
      {/* ── Filtros ── */}
      <Row gutter={[8, 8]} style={{ marginBottom: 16 }} align="middle">
        <Col xs={24} sm={8} md={6}>
          <Input
            prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
            placeholder="Buscar por login o nombre…"
            allowClear
            value={localLogin}
            onChange={(e) => setLocalLogin(e.target.value)}
            onPressEnter={handleSearch}
            onClear={handleSearch}
            style={{ borderRadius: 6 }}
          />
        </Col>
        <Col xs={12} sm={4} md={3}>
          <Select
            placeholder="Entidad"
            allowClear
            style={{ width: "100%" }}
            value={filters.entidad || undefined}
            onChange={(v) => onFiltersChange({ entidad: v || undefined, page: 1 })}
          >
            <Option value="ART">ART</Option>
            <Option value="FAO">FAO</Option>
            <Option value="ADMIN">ADMIN</Option>
          </Select>
        </Col>
        <Col xs={12} sm={4} md={3}>
          <Select
            placeholder="Estado"
            allowClear
            style={{ width: "100%" }}
            value={filters.active || undefined}
            onChange={(v) => onFiltersChange({ active: v || undefined, page: 1 })}
          >
            <Option value="Y">Activo</Option>
            <Option value="N">Inactivo</Option>
          </Select>
        </Col>
        <Col xs={24} sm={8} md={6}>
          <RangePicker
            style={{ width: "100%" }}
            placeholder={["Desde", "Hasta"]}
            value={dateValue}
            onChange={handleDateRange}
            format="DD/MM/YYYY"
          />
        </Col>
        <Col xs={12} sm={4} md={2}>
          <Button icon={<FilterOutlined />} type="primary" ghost onClick={handleSearch}>
            Buscar
          </Button>
        </Col>
        {hasFilters && (
          <Col xs={12} sm={4} md={2}>
            <Tooltip title="Limpiar todos los filtros">
              <Button icon={<ClearOutlined />} onClick={handleClear} type="text" danger>
                Limpiar
              </Button>
            </Tooltip>
          </Col>
        )}
      </Row>

      {/* ── Tabla ── */}
      <Table
        columns={buildColumns(onViewDetail, onExportTrace, permissionExport)}
        dataSource={data}
        rowKey="oid"
        loading={loading}
        size="middle"
        scroll={{ x: 1100 }}
        style={{ borderRadius: 8, overflow: "hidden" }}
        pagination={{
          total,
          current:      filters.page    || 1,
          pageSize:     filters.pageSize || 10,
          showSizeChanger: true,
          showQuickJumper: true,
          pageSizeOptions: ["10", "20", "50", "100", "200"],
          showTotal: (t, [from, to]) => (
            <Text type="secondary" style={{ fontSize: 12 }}>
              Mostrando <strong>{from}–{to}</strong> de <strong>{t}</strong> usuarios
            </Text>
          ),
          onChange: (page, pageSize) => onFiltersChange({ page, pageSize }),
        }}
        locale={{ emptyText: "No se encontraron usuarios con los filtros aplicados" }}
        rowClassName={(_, i) => i % 2 === 0 ? "" : "seguimiento-row-alt"}
      />

      <style>{`
        .seguimiento-row-alt > td { background: #fafafa !important; }
        .ant-table-wrapper .ant-table-thead > tr > th {
          padding: 10px 12px !important;
          font-size: 12px !important;
        }
        .ant-table-wrapper .ant-table-tbody > tr > td {
          padding: 10px 12px !important;
        }
      `}</style>
    </>
  );
};

export default TablaUsuarios;
